/**
 * Estados de página: vazio · carregando · erro (spec 04-TELAS/0.3 §11).
 *
 * A lei do catálogo: vazio e erro SEMPRE com ícone + título + uma linha, e ação QUANDO
 * ela acrescenta alguma coisa.
 *
 * A ressalva é de 31/08/2026, do fundador, olhando a tela: em oito telas o botão do
 * estado vazio era o MESMO botão que já estava no cabeçalho, a dois centímetros dali.
 * Repetir o convite não ajuda a decidir — só cria duas maneiras de fazer a mesma coisa
 * e faz a tela vazia parecer mais cheia do que é. Onde o cabeçalho já oferece a ação,
 * o vazio não a repete.
 *
 * Onde a ação FICA, e por quê: quando ela é a única saída da página ("Voltar para a
 * lista", nas telas de não-encontrado), quando o cabeçalho não a oferece ("Limpar busca
 * e filtros", que só existe porque há filtro ativo) e quando ela leva para outra tela
 * ("Ir para Negócios", quando não há pipeline para prever).
 *
 * Carregando é esqueleto, nunca "spinner no meio da tela" — o esqueleto mostra a
 * forma do que vem e não faz o layout pular quando o dado chega.
 *
 * O estado vazio tem uma responsabilidade que a spec 05 §1 grifa: quando a entidade
 * ativa não tem dado, ele NOMEIA a entidade ("Sem lançamentos na DigitalView
 * México") — nunca se mostra o número de outra entidade sob o rótulo trocado.
 *
 * ## Estes três estados são ANUNCIÁVEIS (lote U1 · achado A11Y-05)
 *
 * Os três trocavam de conteúdo em silêncio para quem usa leitor de tela. Quem enxerga
 * vê o esqueleto pulsando e sabe esperar; quem não enxerga clicava em "Filtrar" e não
 * recebia nada — nem "carregando", nem "não foi possível carregar". A tela parecia
 * travada, e o próximo passo virava adivinhação.
 *
 * `role="status"` no carregamento e no vazio (educado: espera a fala corrente terminar),
 * `role="alert"` no erro (interrompe, porque erro que espera a vez chega tarde demais).
 * O `aria-busy` no esqueleto é o que diz "isto ainda não é o dado" — sem ele, a região
 * viva anunciaria a forma como se fosse conteúdo.
 */
import type { ReactNode } from 'react'
import { Icone } from '../shell/icone.tsx'
import { useTextos } from '../textos.ts'
import { TEXTOS_DOS_ESTADOS } from './periodo-textos.ts'

export function EstadoVazio({
  icone = 'inbox',
  titulo,
  descricao,
  acao,
  semMoldura = false,
}: {
  icone?: string
  titulo: string
  /** Causa ou ação. Sem ela, o vazio fica só com o título (D-203: descrição que repete o título sai). */
  descricao?: string
  acao?: ReactNode
  /** O cartão externo já fornece fundo, borda, raio e sombra. */
  semMoldura?: boolean
}) {
  return (
    <div
      data-teste="estado-vazio"
      role="status"
      className={semMoldura ? 'p-10 text-center' : 'rounded-2xl bg-white border border-hairline shadow-card p-10 text-center'}
    >
      <div className="w-10 h-10 rounded-[10px] bg-navy/5 grid place-items-center text-navy mx-auto mb-3">
        <Icone nome={icone} className="w-5 h-5" />
      </div>
      <p className="text-[15px] font-semibold text-navy">{titulo}</p>
      {descricao !== undefined && descricao !== '' && <p className="text-[13px] text-muted mt-1">{descricao}</p>}
      {acao !== undefined && <div className="mt-4">{acao}</div>}
    </div>
  )
}

/**
 * O vazio DENTRO de um cartão.
 *
 * O `EstadoVazio` acima é de PÁGINA: cartão branco, borda, sombra e `p-10`. Dentro de
 * outro cartão ele vira cartão dentro de cartão, e foi por isso que dezenas de lugares
 * deste app improvisaram um `<p>` solto em vez de usá-lo. Só a Visão Geral chegou a ter
 * QUATRO receitas de "não tem dado" na mesma tela, uma delas em VERDE — e verde diz
 * "deu certo", quando ausência de dado não é sucesso.
 *
 * Esta variante é o mesmo vocabulário na medida do cartão: ícone pequeno em caixa
 * discreta, uma linha de título, uma de apoio, sem moldura própria. O protótipo resolve
 * o mesmo caso com texto solto em `text-muted`; o que se acrescenta aqui é o ícone, que
 * é exatamente o que o fundador apontou ao comparar "Os três calendários" (que tinha)
 * com "Minhas tarefas" (que não tinha).
 */
export function VazioNoCartao({
  icone = 'inbox',
  titulo,
  descricao,
  acao,
}: {
  icone?: string
  titulo: string
  descricao?: string
  acao?: ReactNode
}) {
  return (
    <div data-teste="vazio-no-cartao" role="status" className="flex flex-col items-center gap-2 py-6 text-center">
      <span className="grid h-8 w-8 place-items-center rounded-[8px] bg-navy/[0.06] text-navy">
        <Icone nome={icone} className="h-4 w-4" />
      </span>
      <p className="text-[13px] font-medium text-navy">{titulo}</p>
      {descricao === undefined ? null : <p className="max-w-[42ch] text-[12px] text-muted">{descricao}</p>}
      {acao === undefined ? null : <div className="mt-1">{acao}</div>}
    </div>
  )
}

export function EstadoCarregando({ linhas = 6, semMoldura = false }: { linhas?: number; semMoldura?: boolean }) {
  const texto = useTextos(TEXTOS_DOS_ESTADOS)
  return (
    <div
      data-teste="estado-carregando"
      role="status"
      aria-busy="true"
      className={semMoldura ? 'p-5' : 'rounded-2xl bg-white border border-hairline shadow-card p-5'}
    >
      {/* O texto que o leitor de tela fala; o esqueleto é o que a vista recebe. */}
      <span className="sr-only">{texto('carregando')}</span>
      <div aria-hidden="true" className="animate-pulse space-y-3">
        {Array.from({ length: linhas }, (_, indice) => (
          <div key={indice} className="h-4 rounded bg-paper2" />
        ))}
      </div>
    </div>
  )
}

export function EstadoErro({
  titulo,
  descricao,
  aoTentarDeNovo,
  semMoldura = false,
}: {
  titulo?: string
  descricao: string
  aoTentarDeNovo: () => void
  semMoldura?: boolean
}) {
  const texto = useTextos(TEXTOS_DOS_ESTADOS)
  return (
    <div
      data-teste="estado-erro"
      role="alert"
      className={semMoldura ? 'p-10 text-center' : 'rounded-2xl bg-white border border-hairline shadow-card p-10 text-center'}
    >
      <div className="w-10 h-10 rounded-[10px] bg-red-50 grid place-items-center text-red-600 mx-auto mb-3">
        <Icone nome="triangle-alert" className="w-5 h-5" />
      </div>
      <p className="text-[15px] font-semibold text-navy">{titulo ?? texto('erro')}</p>
      <p className="text-[13px] text-muted mt-1">{descricao}</p>
      <button type="button" className="btn-navy mt-4 h-9 px-4 rounded-[10px] text-sm" onClick={aoTentarDeNovo}>
        {texto('retry')}
      </button>
    </div>
  )
}
